// Korea's home-page area photos and resort photos (Wikimedia Commons; see sites/kr/IMAGE_CREDITS.md).
// Area tiles: 480x325 JPG fallback, 260 px wide WebP (cwebp -q 74) / AVIF (avifenc -q 52).
// Resort photos: 800x450 JPG / WebP (cwebp -q 72) / AVIF (avifenc -q 50).
// No suitable openly licensed winter photo found for: konjiam, jisan, vivaldi, gangchon, oakvalley, high1, o2, edenvalley.
import type { ResponsiveImage, ImageCredit } from '@/site/fallback/data/homeImages';
import capitalJpg from '../assets/home/area-capital.jpg';
import capitalWebp from '../assets/home/area-capital.webp';
import capitalAvif from '../assets/home/area-capital.avif';
import gangwonJpg from '../assets/home/area-gangwon.jpg';
import gangwonWebp from '../assets/home/area-gangwon.webp';
import gangwonAvif from '../assets/home/area-gangwon.avif';
import jeollaJpg from '../assets/home/area-jeolla.jpg';
import jeollaWebp from '../assets/home/area-jeolla.webp';
import jeollaAvif from '../assets/home/area-jeolla.avif';
import gyeongsangJpg from '../assets/home/area-gyeongsang.jpg';
import gyeongsangWebp from '../assets/home/area-gyeongsang.webp';
import gyeongsangAvif from '../assets/home/area-gyeongsang.avif';
import yongpyongJpg from '../assets/home/resort-yongpyong.jpg';
import yongpyongWebp from '../assets/home/resort-yongpyong.webp';
import yongpyongAvif from '../assets/home/resort-yongpyong.avif';
import alpensiaJpg from '../assets/home/resort-alpensia.jpg';
import alpensiaWebp from '../assets/home/resort-alpensia.webp';
import alpensiaAvif from '../assets/home/resort-alpensia.avif';
import phoenixJpg from '../assets/home/resort-phoenix.jpg';
import phoenixWebp from '../assets/home/resort-phoenix.webp';
import phoenixAvif from '../assets/home/resort-phoenix.avif';
import wellihilliJpg from '../assets/home/resort-wellihilli.jpg';
import wellihilliWebp from '../assets/home/resort-wellihilli.webp';
import wellihilliAvif from '../assets/home/resort-wellihilli.avif';
import mujuJpg from '../assets/home/resort-muju.jpg';
import mujuWebp from '../assets/home/resort-muju.webp';
import mujuAvif from '../assets/home/resort-muju.avif';

export const AREA_IMAGES: Record<string, ResponsiveImage> = {
  capital: { jpg: capitalJpg, webp: capitalWebp, avif: capitalAvif },
  gangwon: { jpg: gangwonJpg, webp: gangwonWebp, avif: gangwonAvif },
  jeolla: { jpg: jeollaJpg, webp: jeollaWebp, avif: jeollaAvif },
  gyeongsang: { jpg: gyeongsangJpg, webp: gyeongsangWebp, avif: gyeongsangAvif },
};

export const RESORT_PHOTOS: Record<string, ResponsiveImage> = {
  yongpyong: { jpg: yongpyongJpg, webp: yongpyongWebp, avif: yongpyongAvif },
  alpensia: { jpg: alpensiaJpg, webp: alpensiaWebp, avif: alpensiaAvif },
  phoenix: { jpg: phoenixJpg, webp: phoenixWebp, avif: phoenixAvif },
  wellihilli: { jpg: wellihilliJpg, webp: wellihilliWebp, avif: wellihilliAvif },
  muju: { jpg: mujuJpg, webp: mujuWebp, avif: mujuAvif },
};

export const IMAGE_CREDITS: ImageCredit[] = [
  {
    subject: 'Namhansanseong Fortress in snow, Gyeonggi (Seoul & Gyeonggi area)',
    author: 'Noh Mun Duek',
    license: 'CC BY-SA 4.0',
    licenseUrl: 'https://creativecommons.org/licenses/by-sa/4.0',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Namhansanseong_Fortress_(%EB%82%A8%ED%95%9C%EC%82%B0%EC%84%B1)_with_snow.jpg',
  },
  {
    subject: 'Alpensia Resort ski slopes (Gangwon area, Alpensia Resort)',
    author: 'Republic of Korea from Seoul, Republic of Korea',
    license: 'CC BY-SA 2.0',
    licenseUrl: 'https://creativecommons.org/licenses/by-sa/2.0',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Alpensia_20170202_02_(31847576573).jpg',
  },
  {
    subject: 'Seolcheonbong summit, Muju Deogyusan Resort (Jeolla area, Muju Deogyusan Resort)',
    author: 'Mg shin',
    license: 'CC BY-SA 4.0',
    licenseUrl: 'https://creativecommons.org/licenses/by-sa/4.0',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:%EB%8D%95%EC%9C%A0%EC%82%B0_%EC%84%A4%EC%B2%9C%EB%B4%89.jpg',
  },
  {
    subject: 'Ganwoljae, Yeongnam Alps, in winter (Gyeongsang area)',
    author: 'SCILLA KIM from Busan, Republic of Korea',
    license: 'CC BY 2.0',
    licenseUrl: 'https://creativecommons.org/licenses/by/2.0',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Ganwoljae_Yeongnam_Alps_(24993988016).jpg',
  },
  {
    subject: 'Mona Yongpyong ski slopes',
    author: 'Kalatpadai',
    license: 'CC0',
    licenseUrl: 'http://creativecommons.org/publicdomain/zero/1.0/deed.en',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Ski_slopes_of_YongPyong_Resort,_2013-03-10.jpg',
  },
  {
    subject: 'Phoenix Park slopes',
    author: 'Steven Williams',
    license: 'CC BY-SA 3.0',
    licenseUrl: 'https://creativecommons.org/licenses/by-sa/3.0',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Bogwang_Phoenix_Park_03.jpg',
  },
  {
    subject: 'Welli Hilli Park snow park',
    author: 'Piotrus',
    license: 'CC BY-SA 3.0',
    licenseUrl: 'https://creativecommons.org/licenses/by-sa/3.0',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Wellihillipark_Snow_Park,_Hoengseong_County_12.JPG',
  },
];
